import { useCallback, useState } from 'react';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { gql, useMutation } from '@apollo/client';
import { useLocation } from 'react-router';

import Button from '../inputs/Button';
import LoadingSpinner from '../LoadingSpinner';
import ErrorText from '../ErrorText';
import { isStripePaymentRequired } from '../../utils/featureFlags';

const STRIPE_PK = import.meta.env.VITE_STRIPE_PK;
const stripePromise = loadStripe(STRIPE_PK);

const CREATE_SETUP_INTENT = gql`
  mutation CreateSetupIntent {
    createSetupIntent {
      client_secret
    }
  }
`;

function Form({ urlParamsToPersist }) {
  const [formReady, setFormReady] = useState(false);
  const [formError, setFormError] = useState();

  const stripe = useStripe();
  const elements = useElements();
  const location = useLocation();

  async function handleSubmit(e) {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setFormReady(false);

    const origin = new URL(window.location).origin;
    const curPath = location.pathname;

    const urlParams = new URLSearchParams({
      addPaymentMethod: 'confirming',
      ...urlParamsToPersist,
    });

    const { error } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${origin}${curPath}?${urlParams}`,
      },
    });

    if (error) {
      setFormError(error.message);
      setFormReady(true);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="relative">
      <div
        className={`${
          formReady
            ? ''
            : 'absolute inset-0 h-52 w-full animate-pulse bg-gray-300 rounded-lg'
        }`}
      ></div>
      <PaymentElement
        onReady={() =>
          setTimeout(() => {
            setFormReady(true);
          }, 200)
        }
      />
      {formError && (
        <div className="mx-auto text-error mt-3 -mb-9">
          <p>{formError}</p>
        </div>
      )}
      <Button
        secondary
        className="mt-14"
        type="submit"
        disabled={!stripe || !elements}
      >
        Add card
      </Button>
    </form>
  );
}

export default function AddPaymentMethod({ className, urlParamsToPersist }) {
  const [createSetupIntent, { loading, error, data }] =
    useMutation(CREATE_SETUP_INTENT);
  const setupIntent = data?.createSetupIntent;

  const handleAddCardClick = useCallback(() => {
    createSetupIntent();
  }, [createSetupIntent]);

  if (!isStripePaymentRequired()) {
    return null;
  }

  return (
    <div className={`${className ? className : ''}`}>
      <p className="font-semibold text-lg mb-4">Add payment method</p>
      {!loading && !data && (
        <Button type="button" secondary onClick={handleAddCardClick}>
          Add new card
        </Button>
      )}
      {loading && <LoadingSpinner />}
      {error && <ErrorText message={error.message} />}
      {!loading && !error && setupIntent?.client_secret && (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret: setupIntent.client_secret,
          }}
        >
          <Form urlParamsToPersist={urlParamsToPersist} />
        </Elements>
      )}
    </div>
  );
}
